
import { Component, Vue } from "vue-property-decorator";
import {
  getKeycloakLoginRedirectUri,
  rememberPostLoginPath,
  PORTAL_POST_LOGIN_PATH_KEY,
} from "@/helpers/portal-auth-redirect";
import Footer from "@/components/Footer/Footer.vue";
import imgCalendarHero from "@/assets/img/DfB_Calendar.png";
import imgClientsHistory from "@/assets/img/DfB_Clients_history.png";
import imgJobs from "@/assets/img/DfB_Jobs.png";
import imgMap from "@/assets/img/DfB_Map.png";
import imgDfbLogo from "@/assets/img/dfb_logo.webp";

const WELCOME_FEATURES: { key: string; icon: string; heroSrc: string }[] = [
  {
    key: "feature_clients",
    icon: "mdi-account-group-outline",
    heroSrc: imgClientsHistory,
  },
  {
    key: "feature_installations",
    icon: "mdi-home-city-outline",
    heroSrc: imgMap,
  },
  {
    key: "feature_jobs",
    icon: "mdi-briefcase-outline",
    heroSrc: imgJobs,
  },
  {
    key: "feature_appointments",
    icon: "mdi-calendar-clock",
    heroSrc: imgCalendarHero,
  },
];

const DOMX_CONTACT_UTM_PARAMS =
  "?utm_source=domx_business_portal&utm_medium=cta&utm_campaign=welcome_page&utm_content=get_started";

function domxMarketingContactUrlForLocale(locale: string): string {
  const base =
    locale === "en"
      ? "https://domx.io/domx-for-business/"
      : "https://domx.io/el/domx-for-business-el/";
  return `${base}${DOMX_CONTACT_UTM_PARAMS}#contact-form`;
}

@Component({
  name: "MarketingLanding",
  components: { Footer },
})
export default class MarketingLanding extends Vue {
  activeHeroKey = "feature_appointments";

  /** While true, hero shows the image for `activeHeroKey` unblurred. When false, calendar + blur. */
  heroSharpAfterInteraction = false;

  get domxMarketingContactUrl(): string {
    return domxMarketingContactUrlForLocale(String(this.$i18n.locale || "el"));
  }

  get dfbLogoSrc(): string {
    return imgDfbLogo;
  }

  get welcomeFeatureItems(): { key: string; icon: string; heroSrc: string }[] {
    return WELCOME_FEATURES;
  }

  get heroStageImageSrc(): string {
    if (!this.heroSharpAfterInteraction) {
      return imgCalendarHero;
    }
    const row = WELCOME_FEATURES.find((f) => f.key === this.activeHeroKey);
    return row?.heroSrc ?? imgCalendarHero;
  }

  /** Vue transition key: blurred default vs sharp feature preview. */
  get heroImgTransitionKey(): string {
    if (!this.heroSharpAfterInteraction) {
      return "hero-blurred-calendar";
    }
    return `hero-sharp-${this.activeHeroKey}`;
  }

  /** Softer blur on first paint; none while a tile is selected (sharp). */
  get heroImageFilter(): string {
    if (this.heroSharpAfterInteraction) {
      return "saturate(1.04)";
    }
    return "blur(2px) saturate(1.04)";
  }

  selectHeroFeature(key: string): void {
    if (this.heroSharpAfterInteraction && this.activeHeroKey === key) {
      this.heroSharpAfterInteraction = false;
      return;
    }
    this.activeHeroKey = key;
    this.heroSharpAfterInteraction = true;
  }

  get iosStoreUrl(): string {
    const u = String(process.env.VUE_APP_DOMX_IOS_APP_URL || "").trim();
    return u || "https://apps.apple.com/gr/app/domx-for-business/id1631978188";
  }

  get playStoreUrl(): string {
    const u = String(process.env.VUE_APP_DOMX_ANDROID_APP_URL || "").trim();
    return (
      u ||
      "https://play.google.com/store/apps/details?id=com.io.gastools.technician"
    );
  }

  created(): void {
    const kc = (this as any).$keycloak;
    if (kc?.authenticated) {
      this.$router.replace({ name: "Dashboard" }).catch(() => undefined);
    }
  }

  mounted(): void {
    this.updatePageMeta();
  }

  setLang(code: string): void {
    const loc = code === "en" ? "en" : "el";
    this.$i18n.locale = loc;
    localStorage.setItem("lang", loc);
    try {
      this.$vuetify.lang.current = loc;
    } catch {
      /* ignore */
    }
    this.updatePageMeta();
  }

  /** Keep document title + meta tags aligned with the active welcome-page locale. */
  private updatePageMeta(): void {
    const locale = this.$i18n.locale === "en" ? "en" : "el";
    const title = String(this.$t("landing.seoTitle"));
    const description = `${this.$t("landing.welcomeHeadlineSub1")} ${this.$t("landing.welcomeHeadlineSub2")}`;

    document.title = title;
    document.documentElement.lang = locale;

    this.setMetaContent("name", "description", description);
    this.setMetaContent("property", "og:title", title);
    this.setMetaContent("property", "og:description", description);
    this.setMetaContent(
      "property",
      "og:locale",
      locale === "en" ? "en_US" : "el_GR"
    );
    this.setMetaContent("name", "twitter:title", title);
    this.setMetaContent("name", "twitter:description", description);
  }

  private setMetaContent(
    attr: "name" | "property",
    key: string,
    content: string
  ): void {
    let el = document.querySelector(
      `meta[${attr}="${key}"]`
    ) as HTMLMetaElement | null;
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute(attr, key);
      document.head.appendChild(el);
    }
    el.content = content;
  }

  goToLogin(): void {
    const kc = (this as any).$keycloak;
    if (!sessionStorage.getItem(PORTAL_POST_LOGIN_PATH_KEY)) {
      rememberPostLoginPath("/dashboard");
    }
    if (kc) {
      kc.login({ redirectUri: getKeycloakLoginRedirectUri() });
    }
  }
}
